import React, { useEffect, useMemo, useState } from 'react';
import {
  Grid,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  IconButton
} from '@mui/material';
import { Close, DeleteForeverOutlined } from '@mui/icons-material';
import LinearProgress from '@mui/material/LinearProgress';
import NivelUsuarioEnum from 'shared/Enums/NivelUsuarioEnum';
import migrarArquivoLegadoParaS3 from 'api/documento';
import ButtonMain from './ButtonMain';
import PDFIcon from '../../../assets/img/PDF_2.svg';
import TXTIcon from '../../../assets/img/TXT.svg';
import XLSIcon from '../../../assets/img/XLS.svg';
import PPTIcon from '../../../assets/img/PPT.svg';
import JPGIcon from '../../../assets/img/JPG.svg';
import PNGIcon from '../../../assets/img/PNG.svg';
import CSVIcon from '../../../assets/img/CSV.svg';
import DOCIcon from '../../../assets/img/DOC.svg';
import ZIPIcon from '../../../assets/img/ZIP.svg';
import RARIcon from '../../../assets/img/RAR.svg';
import XMLIcon from '../../../assets/img/XML.svg';
import HTMLIcon from '../../../assets/img/HTML.svg';
import FILEIcon from '../../../assets/img/ARQUIVO.svg';
import { getClienteAtual, getClienteStatus, getDadosUsuario } from '../../../shared';
import LoaderMain from './LoaderMain';

const BoxArquivo = ({
  file,
  funcaoDeletar,
  permitirDeletar,
  loadingUpload,
  isPending,
  onAcaoSemBloqueio,
  estadoUltimaPPC = '',
  documentosAtualizados = null
}) => {
  const [modalExcluirArquivo, setModalExcluirArquivo] = useState(false);
  const [isPendingDownload, setPendingDownload] = useState(false);
  const [msgErro, setMsgErro] = useState('');
  const [fileExtension, setFileExtension] = useState(FILEIcon);

  const infoUsuario = getDadosUsuario();
  const clienteInfo = getClienteStatus();
  const clienteAtual = getClienteAtual();

  const getFileExtension = fileName => {
    if (!fileName) return '';
    return String(fileName)
      .split('.')
      .pop()
      .toLowerCase();
  };

  useEffect(() => {
    if (file && file.nome_arquivo) {
      const icon = getFileExtension(file.nome_arquivo);
      const map = {
        pdf: PDFIcon,
        txt: TXTIcon,
        xls: XLSIcon,
        xlsx: XLSIcon,
        ppt: PPTIcon,
        pptx: PPTIcon,
        doc: DOCIcon,
        docx: DOCIcon,
        jpg: JPGIcon,
        jpeg: JPGIcon,
        png: PNGIcon,
        csv: CSVIcon,
        zip: ZIPIcon,
        rar: RARIcon,
        xml: XMLIcon,
        html: HTMLIcon
      };
      setFileExtension(map[icon] ? map[icon] : FILEIcon);
    }
  }, [file && file.nome_arquivo]);

  const abrirArquivo = async () => {
    if (
      infoUsuario &&
      (infoUsuario.nivel === NivelUsuarioEnum.PROSPECT || infoUsuario.nivel === NivelUsuarioEnum.CLIENTE)
    ) {
      if (file.localizacao === 'S') {
        window.open(file.link_arquivo, '_blank');
        return;
      } else {
        setPendingDownload(true);
        const url = await migrarArquivoLegadoParaS3(file.codigo, clienteAtual && clienteAtual.cnpj).then(
          response => {
            return response.data;
          }
        );
        setPendingDownload(false);
        if (url) window.open(url, '_blank');
        return;
      }
    }
  };

  const fileSize =
    file && file.tamanho_arquivo && file.tamanho_arquivo !== 0 ? file.tamanho_arquivo / 1000 : 0;

  const regrasProntas = useMemo(() => {
    if (documentosAtualizados === null || !clienteAtual || !file) return false;
    return true;
  }, [documentosAtualizados, clienteAtual, file]);

  const calc = useMemo(() => {
    if (!regrasProntas) {
      return {
        podeExibirAcoes: false,
        botaoDesabilitado: true,
        motivoExibirBotao: null,
        condBloqueiaAcoesPorLimite: true
      };
    }

    const condBloqueiaAcoesPorLimite =
      clienteAtual &&
      clienteAtual.limiteVencendo === 'S' &&
      documentosAtualizados === 0 &&
      estadoUltimaPPC === 'FIM';

    const statusStr = String(file && file.status);
    const condStatusBloqueado = statusStr === 'aprovado' || statusStr === 'analise';

    const estadoPpc = clienteInfo && clienteInfo.ppc_status && clienteInfo.ppc_status.estado_ppc;
    const condPpcNaoFim = estadoPpc !== 'FIM';

    const qtdReprov = Number(clienteAtual && clienteAtual.qtd_documento_reprovado) || 0;
    const condDocReprovadoAntigo = qtdReprov > 0;

    const passaNovaRegra = qtdReprov === 0 ? true : qtdReprov > 1 && !condStatusBloqueado;

    let motivo = null;

    if (!condBloqueiaAcoesPorLimite && passaNovaRegra) {
      if (qtdReprov === 0) {
        if (condPpcNaoFim) motivo = 'ppc_nao_fim';
        else if (condDocReprovadoAntigo) motivo = 'doc_reprovado';
      } else {
        if (qtdReprov > 1) motivo = 'doc_reprovado_maior_1';
      }
    }

    const permiteExcluir = Boolean(motivo) && !condStatusBloqueado;
    const podeExibirAcoes = (permiteExcluir || condBloqueiaAcoesPorLimite) && Boolean(permitirDeletar);
    const botaoDesabilitado = Boolean(isPending) || (loadingUpload !== false && estadoPpc !== 'FIM');

    return {
      podeExibirAcoes: podeExibirAcoes,
      botaoDesabilitado: botaoDesabilitado,
      motivoExibirBotao: motivo,
      condBloqueiaAcoesPorLimite: condBloqueiaAcoesPorLimite
    };
  }, [
    regrasProntas,
    clienteAtual,
    documentosAtualizados,
    file && file.status,
    clienteInfo && clienteInfo.ppc_status && clienteInfo.ppc_status.estado_ppc,
    permitirDeletar,
    isPending,
    loadingUpload
  ]);

  const podeExibirAcoes = calc.podeExibirAcoes;
  const botaoDesabilitado = calc.botaoDesabilitado;
  const motivoExibirBotao = calc.motivoExibirBotao;
  const condBloqueiaAcoesPorLimite = calc.condBloqueiaAcoesPorLimite;

  return (
    <Grid container sx={isPendingDownload ? { position: 'relative' } : {}}>
      {isPendingDownload && <LoaderMain tipoLoader="float" tipoImagem="circulo" />}

      <Grid item xl={2} lg={2} md={3} sm={3} xs={2} className="flex-box-items-centralizados">
        <img
          src={fileExtension}
          alt={'arquivo ' + getFileExtension(file && file.nome_arquivo)}
          width="35"
        />
      </Grid>

      <Grid
        item
        xl={8}
        lg={8}
        md={7}
        sm={7}
        xs={8}
        className="texto-esquerda pad-top-0 items-centro-verticalmente"
        sx={{ pl: '5px' }}
      >
        <Typography
          variant="subtitle1"
          className={
            'texto-cor-preto w-100 texto-hidden-overflow texto-negrito ' +
            (file && file.link_arquivo ? 'link-texto' : '')
          }
          onClick={abrirArquivo}
        >
          {file && file.nome_arquivo}
        </Typography>

        <Typography variant="subtitle1" className="texto-cor-cinza-claro font-size-0-875rem w-100">
          {file && file.tamanho_arquivo && !isNaN(file.tamanho_arquivo) ? (
            <>
              {fileSize.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              Kb
            </>
          ) : file && file.tamanho_arquivo && isNaN(file.tamanho_arquivo) ? (
            <>{file.tamanho_arquivo}</>
          ) : (
            ''
          )}
        </Typography>

        {loadingUpload !== false && (
          <LinearProgress
            variant="determinate"
            value={Number(loadingUpload) || 0}
            className="progressbar-main progressbar-primary"
          />
        )}
      </Grid>
      {clienteAtual &&
      clienteAtual.limiteVencendo === 'S' &&
      file &&
      file.status !== 'aprovado' &&
      file.status !== 'analise' &&
      estadoUltimaPPC !== 'FIM' ? (
        <Grid item xl={2} lg={2} md={2} sm={2} xs={2} sx={{ pr: '5px' }}>
          <ButtonMain
            tipoBotao="transparente"
            disabled={botaoDesabilitado}
            classesAdicional="min-width-none texto-cor-cinza-2 btn-deletar-arquivo"
            onClick={() => setModalExcluirArquivo(true)}
            data-motivo={motivoExibirBotao}
          >
            <DeleteForeverOutlined />
          </ButtonMain>
        </Grid>
      ) : (
        (regrasProntas &&
          podeExibirAcoes &&
          file &&
          file.status === 'reprovado' &&
          estadoUltimaPPC !== 'FIM') ||
        (condBloqueiaAcoesPorLimite && (
          <Grid item xl={2} lg={2} md={2} sm={2} xs={2} sx={{ pr: '5px' }}>
            <ButtonMain
              tipoBotao="transparente"
              disabled={botaoDesabilitado}
              classesAdicional="min-width-none texto-cor-cinza-2 btn-deletar-arquivo"
              onClick={() => setModalExcluirArquivo(true)}
              data-motivo={motivoExibirBotao}
            >
              <DeleteForeverOutlined />
            </ButtonMain>
          </Grid>
        ))
      )}

      {modalExcluirArquivo && (
        <Dialog
          open={modalExcluirArquivo}
          keepMounted
          onClose={() => setModalExcluirArquivo(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title" className="texto-cor-principal texto-negrito">
            Excluir documento
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Você confirma a exclusão do documento?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Grid container>
              <Grid item xl={4} lg={4} md={4} sm={2} />
              <Grid item xl={4} lg={4} md={4} sm={5} xs={6}>
                <ButtonMain tipoBotao="transparente" onClick={() => setModalExcluirArquivo(false)}>
                  Cancelar
                </ButtonMain>
              </Grid>
              <Grid item xl={4} lg={4} md={4} sm={5} xs={6}>
                <ButtonMain
                  tipoBotao="azul-escuro"
                  onClick={() => {
                    if (!condBloqueiaAcoesPorLimite && typeof onAcaoSemBloqueio === 'function') {
                      onAcaoSemBloqueio();
                      setModalExcluirArquivo(false);
                      // return;
                    }
                    if (typeof funcaoDeletar === 'function') funcaoDeletar();
                    setModalExcluirArquivo(false);
                  }}
                >
                  Confirmar
                </ButtonMain>
              </Grid>
            </Grid>
          </DialogActions>
        </Dialog>
      )}

      {msgErro !== '' && (
        <Alert
          severity="error"
          className="texto-danger texto-negrito font-size-0-8rem pad-topbot-0 pad-right-left-15"
          action={
            <IconButton size="small" onClick={() => setMsgErro('')}>
              <Close />
            </IconButton>
          }
        >
          {msgErro}
        </Alert>
      )}
    </Grid>
  );
};

export default BoxArquivo;
